import { useCallback, useEffect, useState } from 'react';
import { Gauge, User, Wrench, FileText } from 'lucide-react';
import Button from '../ui/Button';
import sprayService from '../../services/spray';
import { useOrgTimezone, formatDateInTz } from '../../utils/timezone';
import { toNum } from '@/utils/numeric';
import type { EquipmentCalibration } from '../../types/spray';

interface CalibrationHistoryProps {
  equipmentId: number;
  onCalibrate?: () => void;
  /** Bump to force a reload after a new calibration is recorded. */
  refreshKey?: number;
}

function methodLabel(m: EquipmentCalibration['method']): string {
  return m === 'nozzle' ? 'Single jet' : 'Timed tank';
}

/** What was actually measured, so a reader can check the arithmetic. */
function measurementSummary(c: EquipmentCalibration): string {
  if (c.method === 'nozzle') {
    return `${toNum(c.nozzle_flow_lpm)} L/min × ${c.nozzle_count} nozzles`;
  }
  const secs = c.time_to_empty_sec ?? 0;
  const mins = Math.floor(secs / 60);
  const rem = secs % 60;
  return `${toNum(c.test_volume_l)} L in ${mins}m ${rem.toString().padStart(2, '0')}s`;
}

/**
 * Every calibration this machine has had, newest first.
 *
 * The point is comparability: a rate that has drifted between calibrations is
 * the signal that something has worn or blocked, and it is only visible if the
 * old figures are kept. The previous design overwrote them in place, so there
 * was nothing to compare against.
 */
function CalibrationHistory({ equipmentId, onCalibrate, refreshKey = 0 }: CalibrationHistoryProps) {
  const timezone = useOrgTimezone();
  const [rows, setRows] = useState<EquipmentCalibration[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await sprayService.getCalibrations(equipmentId);
    if (res.success && res.data) setRows(res.data);
    setLoading(false);
  }, [equipmentId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-neutral-800 flex items-center gap-2">
          <Gauge className="h-5 w-5 text-primary-600" />
          Calibration History
        </h2>
        {onCalibrate && (
          <Button variant="outline" size="sm" onClick={onCalibrate}>
            <Wrench className="h-4 w-4" />
            Calibrate
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-neutral-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-neutral-600">
          This sprayer has never been calibrated. Until it is, any water volume or tank count
          for it comes from figures someone typed rather than from anything the machine has
          been measured doing.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((c, idx) => {
            const rate = toNum(c.output_rate_lpha);
            const prevRate = idx < rows.length - 1 ? toNum(rows[idx + 1].output_rate_lpha) : null;
            // Drift against the previous calibration is the whole reason to
            // keep them: a machine putting out 15% more than last time has
            // something worn or blocked.
            const driftPct = rate != null && prevRate != null && prevRate > 0
              ? ((rate - prevRate) / prevRate) * 100
              : null;

            return (
              <div key={c.id} className="rounded-lg border border-neutral-200 px-3 py-2.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-neutral-900">
                      {rate ?? '—'} L/ha
                      <span className="font-normal text-neutral-500">
                        {' · '}{toNum(c.total_flow_lpm)} L/min at {toNum(c.speed_kph)} km/h
                      </span>
                    </p>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      {methodLabel(c.method)}: {measurementSummary(c)}
                      {c.swath_source === 'strip' ? ' · weed strip ' : ' · swath '}{toNum(c.reference_swath_m)} m
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-neutral-500">
                      {formatDateInTz(c.calibrated_at, timezone)}
                    </p>
                    {driftPct != null && Math.abs(driftPct) >= 5 && (
                      <p className={`text-xs font-medium ${Math.abs(driftPct) >= 15 ? 'text-amber-600' : 'text-neutral-500'}`}>
                        {driftPct > 0 ? '+' : ''}{driftPct.toFixed(0)}% vs previous
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 mt-1.5 text-xs text-neutral-500">
                  {(c.calibrated_by_first_name || c.calibrated_by_last_name) && (
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {c.calibrated_by_first_name} {c.calibrated_by_last_name}
                    </span>
                  )}
                  {c.operating_pressure_bar != null && <span>{toNum(c.operating_pressure_bar)} bar</span>}
                  {c.operating_rpm != null && <span>{c.operating_rpm} rpm</span>}
                  {c.spray_reference_number && (
                    <span className="flex items-center gap-1">
                      <FileText className="h-3 w-3" />
                      for {c.spray_reference_number}
                    </span>
                  )}
                </div>

                {c.notes && <p className="text-xs text-neutral-600 mt-1">{c.notes}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CalibrationHistory;
